import { apiClient } from '@copro/api';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useSpace } from '../../../hooks/useSpace';
import { GlobalSearchResults, SearchResultItem } from '../types';
import { keys } from './keys';

export async function fetchGlobalSearch(spaceId: string, spaceSlug: string, query: string): Promise<GlobalSearchResults> {
  const cleanQuery = query.trim();
  if (cleanQuery.length < 2) {
    return {
      subTopics: [],
      providers: [],
      documents: [],
      meetings: [],
      totalCount: 0,
    };
  }

  const basePath = `/spaces/${spaceSlug}`;
  const searchPattern = `%${cleanQuery}%`;

  const [topicsRes, providersRes, docsRes, meetingsRes] = await Promise.all([
    // Search sub-topics
    apiClient
      .from('sub_topics')
      .select('id, name, state, topics!inner(space_id)')
      .eq('topics.space_id', spaceId)
      .ilike('name', searchPattern)
      .limit(6),

    // Search providers
    apiClient
      .from('providers')
      .select('id, name, description')
      .eq('space_id', spaceId)
      .ilike('name', searchPattern)
      .limit(6),

    // Search documents
    apiClient
      .from('documents')
      .select('id, name, file_url')
      .eq('space_id', spaceId)
      .ilike('name', searchPattern)
      .limit(6),

    // Search council meetings
    apiClient
      .from('council_meetings')
      .select('id, title, start_time')
      .eq('space_id', spaceId)
      .ilike('title', searchPattern)
      .limit(6),
  ]);

  const subTopics: SearchResultItem[] = (topicsRes.data ?? []).map((st) => ({
    id: st.id,
    type: 'sub_topic',
    title: st.name,
    subtitle: st.state === 'closed' ? 'Clôturé' : 'En cours',
    url: `${basePath}/topics/${st.id}`,
    badge: st.state === 'closed' ? 'Clôturé' : undefined,
  }));

  const providers: SearchResultItem[] = (providersRes.data ?? []).map((p) => ({
    id: p.id,
    type: 'provider',
    title: p.name,
    subtitle: p.description || 'Prestataire',
    url: `${basePath}/providers/${p.id}`,
  }));

  const documents: SearchResultItem[] = (docsRes.data ?? []).map((d) => ({
    id: d.id,
    type: 'document',
    title: d.name ?? 'Document sans nom',
    subtitle: 'Document',
    url: d.file_url ?? `${basePath}/documents`,
  }));

  const meetings: SearchResultItem[] = (meetingsRes.data ?? []).map((m) => {
    let subtitle = 'Réunion de conseil';
    if (m.start_time) {
      try {
        subtitle = format(new Date(m.start_time), 'd MMMM yyyy', { locale: fr });
      } catch {
        // Keep default
      }
    }
    return {
      id: m.id,
      type: 'meeting',
      title: m.title ?? 'Réunion de conseil',
      subtitle,
      url: `${basePath}/council/meetings`,
    };
  });

  const totalCount = subTopics.length + providers.length + documents.length + meetings.length;

  return {
    subTopics,
    providers,
    documents,
    meetings,
    totalCount,
  };
}

export function useGlobalSearch(query: string) {
  const { currentSpace } = useSpace();
  const spaceId = currentSpace?.id ?? '';
  const spaceSlug = currentSpace?.slug ?? '';

  const cleanQuery = query.trim();

  return useQuery({
    queryKey: keys.query(spaceId, cleanQuery),
    queryFn: () => fetchGlobalSearch(spaceId, spaceSlug, cleanQuery),
    enabled: !!spaceId && cleanQuery.length >= 2,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

